import React, { useState } from 'react';
import Uppy from '@uppy/core';
import { UppyContextProvider } from '@uppy/react';
import AwsS3 from '@uppy/aws-s3';
import ThumbnailGenerator from '@uppy/thumbnail-generator';

import {
  APIupdateAsset,
  APIdeleteAsset,
} from '../../api/Assets';

import Dropzone from './Dropzone';
import FilesGrid from './FilesGrid';
import MemorySprkFiles from './SprkFiles';

import {
  getFileMetadata,
  isImageType,
} from '../../helpers/file';

import '@uppy/core/css/style.css';
import '@uppy/react/css/style.css';

import type { Asset } from '../../types/asset';

interface MemoryFilesProps {
  errors: any;
  files: Asset[];
  addFile: Function;
  selectFile: Function;
  removeFile: Function;
  setUploading: Function;
  setFiles: Function;
  variant?: 'memory' | 'sprk';
  sharedAssets: Asset[];
  selectedAssets: string[];
  inTimeline?: boolean;
}

const MemoryFiles: React.FC<MemoryFilesProps> = ({
  errors,
  files,
  addFile,
  selectFile,
  removeFile,
  setUploading,
  setFiles,
  variant,
  sharedAssets,
  selectedAssets,
  inTimeline = false,
}) => {
  const [uppy] = useState(() => new Uppy({
    autoProceed: true,
    restrictions: {
      allowedFileTypes: ['.jpg', '.jpeg', '.png', '.mp4', '.mov', '.avi', '.mp3', '.wav'],
      maxNumberOfFiles: 25,
    },
  })
  .on('file-removed', (file) => {
    APIdeleteAsset(file.meta.uuid as string);
    removeFile(file.meta.uuid);
    setFiles(uppy.getFiles());
  })
  .on('file-added', () => {
    setFiles(uppy.getFiles());
  })
  .on('progress', () => {
    setFiles(uppy.getFiles());
  })
  .on('upload', () => {
    setUploading(true);
  })
  .on('upload-progress', () => {
    setFiles(uppy.getFiles());
  })
  .on('upload-success', async (file) => {
    if (file) {
      const metadata = isImageType(file.type) ? await getFileMetadata(file.data as File) : {};
      APIupdateAsset(file.meta.uuid as string, {
        status: 'uploaded',
        metadata,
      });
      addFile(file.meta.uuid);
    }
    setFiles(uppy.getFiles());
    setUploading(false);
  })
  .on('upload-error', () => {
    setFiles(uppy.getFiles());
    setUploading(false);
  })
  .on('thumbnail:generated', () => {
    setFiles(uppy.getFiles());
  })
  .use(ThumbnailGenerator, {
    thumbnailType: 'image/png',
  })
  .use(AwsS3, {
   shouldUseMultipart: false,
    getUploadParameters: async (file) => {
      return window.axios('/api/assets/upload', {
        method: 'post',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        data: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }),
      })
        .then((response) => {
          return response.data;
        })
        .then((data) => {
          uppy.setFileMeta(file.id, {...data.fields});

          return {
            method: data.method,
            url: data.url,
            fields: data.fields,
            headers: data.headers,
          }
        });
    },
  }));

  const handleAddFile = () => {
    document.getElementById('uppy-dropzone-file-input-uppy')?.click();
  };

  const handleRemoveFile = (fileId: string) => {
    const file = files.find((f) => f.id === fileId);
    if (file?.local) {
      uppy.removeFile(file.uppyFileId as string);
    }
    removeFile(fileId);
  };

  const number = inTimeline ? 1 : 2

  return (
    <div className="memory-files-container inner">
      <div className="memory-files-header">
        <div className="page-eyebrow">
          <wa-avatar initials={number.toString()} />
          {variant === 'sprk' ?
            'Choose an image' :
            'Photos, video & audio'
          }
        </div>
      </div>
      {variant === 'sprk' && (
        <div className="sprk-files">
          <MemorySprkFiles
            sharedAssets={sharedAssets}
            selectedAssets={selectedAssets}
            handleSelectFile={selectFile}
          />
        </div>
      )}
      <div className="memory-files">
        <UppyContextProvider uppy={uppy}>
          <div className="memory-files-uploader">
            <Dropzone variant={variant} />
            <div className="memory-files-thumbnails">
              <FilesGrid
                files={files}
                variant={variant}
                handleAddFile={handleAddFile}
                handleRemoveFile={handleRemoveFile}
              />
              {errors.assets && (
                <div className="error-message">
                  {errors.assets.message}
                </div>
              )}
            </div>
            {(files.length > 0 && variant === 'memory') && (
              <div className="memory-files-limit">
                {files.length} of 25 files used · Free tier
              </div>
            )}
          </div>
        </UppyContextProvider>
      </div>
    </div>
  );
}

export default MemoryFiles;
